import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { HotelProposalReasons } from '../../../types/hotelProposalReasons';
import type { HotelProposalItem } from '../../../types/proposalBundles';

type ReasonEntry = {
  itemId: number;
  title: string | null;
  reason: string;
  itemNumber: number;
};

interface Props {
  items: HotelProposalItem[];
  reasons: HotelProposalReasons;
  showSummary?: boolean;
  showHeader?: boolean;
  compact?: boolean;
}

const HotelRecommendationReasonsSection = ({
  items,
  reasons,
  showSummary = true,
  showHeader = true,
  compact = false
}: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme, compact), [theme, compact]);
  const { status, result } = reasons;

  const orderedReasons = useMemo(() => {
    if (!result) return [];
    const reasonByItemId = new Map(result.itemReasons.map(entry => [entry.itemId, entry.reason]));
    return items
      .map((item, index) => {
        const reason = reasonByItemId.get(item.id);
        if (!reason) return null;
        return { itemId: item.id, title: item.title, reason, itemNumber: index + 1 };
      })
      .filter((entry): entry is ReasonEntry => entry !== null);
  }, [result, items]);

  if (items.length === 0) return null;
  if (status === 'idle' || status === 'loading') {
    if (compact && !showSummary) return null;
    return <div style={styles.loading}>提案理由を読み込み中…</div>;
  }
  if (status !== 'completed' || !result) return null;
  if (orderedReasons.length === 0 && !showSummary) return null;
  if (orderedReasons.length === 0 && !result.summaryReason) return null;

  return (
    <div style={styles.container}>
      {showHeader && <div style={styles.header}>AIによるホテル提案の理由</div>}
      {showSummary && result.summaryReason && <div style={styles.summary}>{result.summaryReason}</div>}
      {orderedReasons.length > 0 && (
        <ul style={styles.list}>
          {orderedReasons.map(entry => (
            <li key={entry.itemId} style={styles.listItem}>
              <span style={styles.circleNumber}>{entry.itemNumber}</span>
              <div style={styles.listBody}>
                {entry.title && <div style={styles.itemTitle}>{entry.title}</div>}
                <div style={styles.itemReason}>{entry.reason}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const getStyles = (theme: AITTheme, compact: boolean) => ({
  loading: {
    marginTop: compact ? '0' : '12px',
    padding: compact ? '10px 14px' : '0',
    fontSize: '12px',
    color: theme.grayTextColor
  },
  container: {
    marginTop: compact ? '0' : '16px',
    padding: '12px 14px',
    backgroundColor: '#f8fbfd',
    borderRadius: compact ? '0' : '6px',
    ...(compact
      ? { borderTop: `1px solid ${theme.grayBorderColor}` }
      : { border: `1px solid ${theme.grayBorderColor}` })
  },
  header: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.linkColor,
    marginBottom: '8px'
  },
  summary: {
    fontSize: '12px',
    lineHeight: 1.6,
    color: theme.textColor,
    marginBottom: '10px'
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px'
  },
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px'
  },
  circleNumber: {
    width: '22px',
    height: '22px',
    minWidth: '22px',
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700 as const,
    fontSize: '11px'
  },
  listBody: {
    flex: 1
  },
  itemTitle: {
    fontSize: '12px',
    fontWeight: 600 as const,
    color: theme.textColor,
    marginBottom: '2px'
  },
  itemReason: {
    fontSize: '12px',
    lineHeight: 1.6,
    color: theme.textColor
  }
});

export default HotelRecommendationReasonsSection;
